import { take, put, select, call, all, fork } from 'redux-saga/effects';
import set from 'lodash.set';
import { saveAs } from 'file-saver';
import uuid from 'uuid/v4';
import get from 'lodash.get';

import {
    ApplicationAccessData,
    ApplicationId,
    ApplicationIdentityDefinition,
    ApplicationTrustedDomainValue,
    ApplicationThemeDefinition,
} from 'yeself-common/typings/application/Application';

import log from 'yeself-client-common/utils/LogUtils';
import { respawn } from 'yeself-client-common/utils/SagaUtils';
import { digestValue } from 'yeself-client-common/utils/CommonUtils';

import { fetchApplicationList } from 'Console/data/applications/actions';
import { history } from 'Console/configureStore';
import { ROUTES } from 'Console/constants/routes';
import { encryptData } from 'Console/utils/rsaEncrypt';
import { fetchApplicationListTask } from 'Console/data/applications/saga';
import { pushNotificationToStack } from 'Console/data/notification/actions';
import { initializeContentLanguageTask } from 'Console/data/app/saga';
import { selectApplications } from 'Console/data/applications/selectors';
import { selectUserId } from 'Console/data/accounts/selectors';

import {
    UPDATE_DETAIL_SETTINGS,
    FETCH_USERS_ACCESS,
    ACCESS_INVITE_USERS,
    FETCH_LANGUAGE_LIST,
    EXPORT_TRANSLATIONS,
    IMPORT_TRANSLATIONS,
    EXPORT_ADD_ON_TRANSLATIONS,
    IMPORT_ADD_ON_TRANSLATIONS,
    FETCH_ADD_ON_OAUTH_AUTHORIZATION_CODE_URL,
    DELETE_ADDON,
    POST_ADD_ON,
    UPDATE_ADD_ON,
    LOAD_ARTICLES_PICKER_SCRIPT,
    RESET_FACTS_APPLICATION,
    RESET_FACTS_FOR_USER,
    POST_GUIDE_GENERATION,
    GET_APPLICATION_SETTINGS_DATA,
    POST_DUPLICATE_APPLICATION,
    EGG_SERVING_PIPELINE,
} from './SettingsConstants';

import {
    getApplicationSettingsDataSuccess,
    getApplicationSettingsDataError,
    fetchLanguageListSuccess,
    fetchLanguageListError,
    exportTranslationsError,
    importTranslationsError,
    importAddOnTranslationsError,
    exportAddOnTranslationsError,
    fetchUsersAccessData,
    fetchUsersAccessError,
    onInviteNewUsersInAccess,
    inviteNewUsersInAccessError,
    deleteAddOnSuccess,
    deleteAddOnError,
    createNewAddOnError,
    resetFactsApplicationError,
    generateGuideSuccess,
    generateGuideError,
    addOnOauthAuthorizationCodeUrlSuccess,
    resetFactsForUserError,
    duplicateApplicationSuccess,
    duplicateApplicationError,
    updateApplicationAccessFail,
    updateApplicationAccessSuccess,
    fetchInstalledListAddonsSuccess,
    fetchInstalledListAddonsFail,
    fetchAvailableListAddonsSuccess,
    fetchAvailableListAddonsFail,
    updateApplicationIdentitySuccess,
    updateApplicationIdentityFail,
    updateApplicationThemeSuccess,
    updateApplicationThemeFail,
    updateApplicationExtensionUrlsFail,
    updateApplicationExtensionUrlsSuccess,
    updateApplicationTrustedDomainsFail,
    updateApplicationTrustedDomainsSuccess,
    rolloutPoliciesByMyOrganizationsSuccess,
    rolloutPoliciesByMyOrganizationsError,
    rolloutPoliciesBySpaceIdSuccess,
    rolloutPoliciesBySpaceIdError,
} from './SettingsAction';

import {
    selectApplicationSettingsData,
    selectAppId,
    selectOrgId,
    selectAddOnsInstalledList,
    selectApplicationTrackingSecurityHashUserId,
} from './SettingsSelector';

import {
    fetchLanguageList,
    updateApplicationDetail,
    fetchAppUsersListService,
    inviteUser,
    exportTranslationsRequest,
    exportAddOnTranslationsRequest,
    importTranslationsRequest,
    importAddOnTranslationsRequest,
    fetchAddOnAvailableListRequest,
    fetchInstalledListAddOnsRequest,
    fetchAddOnOauthAuthorizationCodeUrlRequest,
    loadArticlesPickerScripRequest,
    getVaultPublicKeyRequest,
    deleteFactsRequest,
    deleteFactsForUserRequest,
    generateGuideRequest,
    duplicateApplicationRequest,
    updateApplicationAccess,
    updateApplicationIdentity,
    updateApplicationTheme,
    updateApplicationOriginUrls,
    updateApplicationTrustedDomains,
    getRolloutPoliciesByMyOrganizations,
    getRolloutPoliciesBySpaceId,
} from './SettingsService';

import {
    FETCH_AVAILABLE_LIST_ADDONS_REQUEST,
    FETCH_INSTALLED_LIST_ADDONS_REQUEST,
    UPDATE_APPLICATION_ACCESS_REQUEST,
    UPDATE_APPLICATION_EXTENSION_URLS_REQUEST,
    UPDATE_APPLICATION_TRUSTED_DOMAINS_REQUEST,
    UPDATE_APPLICATION_IDENTITY_REQUEST,
    UPDATE_APPLICATION_THEME_REQUEST,
    ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_REQUEST,
    ROLLOUT_POLICIES_BY_SPACE_ID_REQUEST,
    ExtensionUrl,
} from './types';

function* getApplicationSettingsDataTask(appId) {
    try {
        const applications = yield select(selectApplications);

        const selectedApplication = applications.find((application) => application.id === appId);

        if (!selectedApplication) {
            yield call(fetchApplicationListTask);
            const actualApplicationsList = yield select(selectApplications);
            const selectedApplication = actualApplicationsList.find(
                (application) => application.id === appId
            );

            if (!selectedApplication) {
                yield put(pushNotificationToStack('Unable to find space'));
                history.push(ROUTES.APPLICATIONS);
            }

            yield put(getApplicationSettingsDataSuccess(selectedApplication));
            yield call(initializeContentLanguageTask);
            return;
        }

        yield put(getApplicationSettingsDataSuccess(selectedApplication));
        yield call(initializeContentLanguageTask);
    } catch (err) {
        log.error(err);
        yield put(getApplicationSettingsDataError());
    }
}

function* getApplicationSettingsDataWatcher() {
    while (true) {
        const { payload } = yield take(GET_APPLICATION_SETTINGS_DATA);
        yield call(getApplicationSettingsDataTask, payload);
    }
}

function* fetchLanguagesListWatcher() {
    while (true) {
        const { payload } = yield take(FETCH_LANGUAGE_LIST);

        try {
            const resp = yield call(fetchLanguageList, payload);
            yield put(fetchLanguageListSuccess(resp.body));
        } catch (err) {
            log.error(err);
            yield put(fetchLanguageListError());
        }
    }
}

function* exportAddOnTranslationsTask(payload, message) {
    const url = get(payload, 'addon.modules.language.exportsUrl');

    try {
        const response = yield call(exportAddOnTranslationsRequest, url);
        const [, filename] = response.header['content-disposition'].match(/filename="(.*)"/);
        saveAs(response.body, filename);
        yield put(pushNotificationToStack(message.success));
    } catch (err) {
        console.error(err);
        yield put(exportAddOnTranslationsError());
        yield put(pushNotificationToStack(message.fail));
    }
}

function* exportAddOnTranslationsWatcher() {
    while (true) {
        const { payload, message } = yield take(EXPORT_ADD_ON_TRANSLATIONS);
        yield fork(exportAddOnTranslationsTask, payload, message);
    }
}

function* exportTranslationsTask(message, format) {
    const appId = yield select(selectAppId);

    try {
        const response = yield call(exportTranslationsRequest, appId, format);
        const [, filename] = response.header['content-disposition'].match(/filename="(.*)"/);
        saveAs(response.body, filename);
    } catch (err) {
        log.error(err);
        yield put(exportTranslationsError());
        yield put(pushNotificationToStack(message.fail));
    }
}

function* exportTranslationsWatcher() {
    while (true) {
        const { message, format } = yield take(EXPORT_TRANSLATIONS);
        yield fork(exportTranslationsTask, message, format);
    }
}

function* importAddOnTranslationsTask({ addon, file }, message) {
    const url = get(addon, 'addon.modules.language.importUrl');

    try {
        yield call(importAddOnTranslationsRequest, url, file);
        yield put(pushNotificationToStack(message.success));
    } catch (err) {
        log.error(err);
        yield put(pushNotificationToStack(message.fail));
        yield put(importAddOnTranslationsError());
    }
}

function* importAddOnTranslationsWatcher() {
    while (true) {
        const { payload, message } = yield take(IMPORT_ADD_ON_TRANSLATIONS);
        yield fork(importAddOnTranslationsTask, payload, message);
    }
}

function* importTranslationsTask({ file }, message) {
    const appId = yield select(selectAppId);
    try {
        const response = yield call(importTranslationsRequest, appId, file);

        if (!response.ok) {
            const blob = new Blob([response.text], { type: response.type });
            saveAs(blob, 'errorLog.json');

            yield put(pushNotificationToStack(message.fail));
            yield put(importTranslationsError());
        }
        yield put(pushNotificationToStack(message.success));
    } catch (err) {
        log.error(err);
        yield put(pushNotificationToStack(message.fail));
        yield put(importTranslationsError());
    }
}

function* importTranslationsWatcher() {
    while (true) {
        const { payload, message } = yield take(IMPORT_TRANSLATIONS);
        yield fork(importTranslationsTask, payload, message);
    }
}

function* updateDetailSettingsTask(payload, message) {
    const appId = yield select(selectAppId);
    const applicationSettingsData = yield select(selectApplicationSettingsData);

    const settingsData = { ...applicationSettingsData };
    Object.entries(payload).forEach(([key, value]) => {
        set(settingsData, key, value);
    });

    const data = Object.entries(settingsData)
        .filter(
            ([key]) =>
                key !== 'access' &&
                key !== 'identity' &&
                key !== 'originUrls' &&
                key !== 'trustedDomains' &&
                key !== 'theme'
        )
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    try {
        yield call(updateApplicationDetail, appId, data);
        yield put(getApplicationSettingsDataSuccess(settingsData));
        yield put(pushNotificationToStack(message.success));
    } catch (err) {
        log.error(err);
        yield put(getApplicationSettingsDataError());
        yield put(pushNotificationToStack(message.fail));
    }
}

function* updateDetailSettingsWatcher() {
    while (true) {
        const { payload, message } = yield take(UPDATE_DETAIL_SETTINGS);
        yield fork(updateDetailSettingsTask, payload, message);
    }
}

function* fetchUsersAccessTask(message) {
    try {
        const organizationId = yield select(selectOrgId);
        const resp = yield call(fetchAppUsersListService, organizationId);
        const data = JSON.parse(resp.text).payload;

        yield put(fetchUsersAccessData(data));
    } catch (err) {
        log.error(err);
        yield put(pushNotificationToStack(message.fail));
        yield put(fetchUsersAccessError());
    }
}

function* fetchUsersAccessWatcher() {
    while (true) {
        const { message } = yield take(FETCH_USERS_ACCESS);
        yield fork(fetchUsersAccessTask, message);
    }
}

function* UsersAccessSaga() {
    yield respawn(fetchUsersAccessWatcher);
}

function* inviteNewUserWatcher() {
    while (true) {
        const { payload, message } = yield take(ACCESS_INVITE_USERS);

        try {
            const appId = yield select(selectAppId);
            const newUserData = {
                emails: payload,
            };
            const resp = yield call(inviteUser, appId, newUserData);
            yield put(onInviteNewUsersInAccess(resp, message.success));
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(inviteNewUsersInAccessError());
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

// @TODO connect to real endpoints once it will be ready
function* fetchAvailableListAddonsRequestTask(applicationId: ApplicationId) {
    try {
        const res = yield call(fetchAddOnAvailableListRequest, applicationId);
        const { data } = JSON.parse(res.text).payload;

        yield put(fetchAvailableListAddonsSuccess(data));
    } catch (err) {
        log.error(err);
        yield put(fetchAvailableListAddonsFail(err));
    }
}

function* fetchAvailableListAddonsRequestWatcher() {
    while (true) {
        const { payload } = yield take(FETCH_AVAILABLE_LIST_ADDONS_REQUEST);
        yield fork(fetchAvailableListAddonsRequestTask, payload);
    }
}

function* fetchInstalledListAddonsRequestTask(applicationId: ApplicationId) {
    try {
        const res = yield call(fetchInstalledListAddOnsRequest, applicationId);
        const { data } = JSON.parse(res.text).payload;

        yield put(fetchInstalledListAddonsSuccess(data));
    } catch (err) {
        log.error(err);
        yield put(fetchInstalledListAddonsFail(err));
    }
}

function* fetchInstalledListAddonsRequestWatcher() {
    while (true) {
        const { payload } = yield take(FETCH_INSTALLED_LIST_ADDONS_REQUEST);
        yield fork(fetchInstalledListAddonsRequestTask, payload);
    }
}

function* fetchAddOnOauthAuthorizationCodeUrlWatcher() {
    while (true) {
        const {
            payload: { applicationId, addonInstanceSystemKey, addonSystemKey },
        } = yield take(FETCH_ADD_ON_OAUTH_AUTHORIZATION_CODE_URL);
        try {
            const res = yield call(
                fetchAddOnOauthAuthorizationCodeUrlRequest,
                applicationId,
                addonSystemKey,
                addonInstanceSystemKey
            );
            const { url, redirectUri } = JSON.parse(res.text);
            yield put(addOnOauthAuthorizationCodeUrlSuccess(addonInstanceSystemKey, url, redirectUri));
        } catch (err) {
            log.error(err);
        }
    }
}

// @TODO add instanceId
function* deleteAddOnWatcher() {
    while (true) {
        const { payload } = yield take(DELETE_ADDON);

        try {
            yield put(deleteAddOnSuccess(payload));
        } catch (err) {
            log.error(err);
            yield put(deleteAddOnError());
        }
    }
}

function* createAddOnWatcher() {
    while (true) {
        const { payload, message } = yield take(POST_ADD_ON);
        const {
            addon: { authType },
        } = payload;

        try {
            const newAddOnData = {
                id: uuid(),
                configuration: {
                    authentication: {
                        type: authType,
                    },
                },
                payload: null,
                vaultedPayload: null,
                addon: {
                    vendor: {
                        url: null,
                    },
                    scopes: null,
                    definitions: {},
                    authentication: {},
                },
            };
            console.error(newAddOnData, 'newAddonData');
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(createNewAddOnError());
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

function* updateAddonWatcher() {
    while (true) {
        const {
            payload: { addOnId, data, message },
        } = yield take(UPDATE_ADD_ON);
        const {
            auth: { clientId, clientSecret, password, username, apiKey },
            authType,
        } = data;

        try {
            const appId = yield select(selectAppId);
            let vaultedPayload = {};
            const keyResp = yield call(getVaultPublicKeyRequest, appId);
            const publicKey = get(keyResp, `body.data.keys.1.public_key`);

            if (!publicKey) {
                throw new Error('Unable to get public key');
            }

            const installedAddons = yield select(selectAddOnsInstalledList);
            const addon = installedAddons.find((installed) => installed.id === addOnId);

            if (authType === 'api_key' && apiKey) {
                vaultedPayload = {
                    api_key: yield encryptData(apiKey, publicKey),
                };
            }
            if (authType === 'username_password' && username && password) {
                vaultedPayload = {
                    user_name: yield encryptData(username, publicKey),
                    password: yield encryptData(password, publicKey),
                };
            }
            if (authType === 'oauth2_authorization_code' && clientId && clientSecret) {
                vaultedPayload = {
                    client_id: yield encryptData(clientId, publicKey),
                    client_secret: yield encryptData(clientSecret, publicKey),
                };
            }

            if (Object.keys(vaultedPayload).length > 0) {
                set(addon, 'configuration.authentication.vaultedPayload', vaultedPayload);
            }

            console.error(addon, 'addon');

            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            yield put(pushNotificationToStack(message.fail));
            log.error(err);
        }
    }
}

function* loadArticlesPickerScriptWatcher() {
    while (true) {
        yield take(LOAD_ARTICLES_PICKER_SCRIPT);
        const appId = yield select(selectAppId);
        try {
            const resp = yield call(loadArticlesPickerScripRequest, appId);
            const tag = document.createElement('script');
            tag.async = false;
            tag.text = resp.text;
            const body = document.getElementsByTagName('body')[0];
            body.appendChild(tag);

            // const resPayload = JSON.parse(resp.text).payload;
            // yield put(loadArticlesPickerScriptRequestSuccess(resPayload));
        } catch (err) {
            log.error(err);
        }
    }
}

// Guide generation
function* generateGuideWatcher() {
    while (true) {
        const { payload, message } = yield take(POST_GUIDE_GENERATION);
        const appId = yield select(selectAppId);
        const userId = yield select(selectUserId);
        const orgId = yield select(selectOrgId);

        try {
            const generateGuide = {
                name: EGG_SERVING_PIPELINE,
                applicationId: appId,
                organisationId: orgId,
                userId,
                idType: payload.idType,
                expertIds: payload.expertIds,
                count: payload.count,
                maximalEventsInSession: payload.maximalEventsInSession,
                minimalSupport: payload.minimalSupport,
                presetSelect: payload.presetSelect,
                modelType: payload.modelType,
                maximalTimeBetweenEvents: payload.maximalTimeBetweenEvents,
                config: payload.config,
                urls: payload.urls,
            };
            yield call(generateGuideRequest, generateGuide);
            yield put(generateGuideSuccess(generateGuide));
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(generateGuideError());
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

function* deleteAllFactsWatcher() {
    while (true) {
        const { payload, message } = yield take(RESET_FACTS_APPLICATION);

        try {
            yield call(deleteFactsRequest, payload);
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(resetFactsApplicationError());
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

function* deleteFactsForUserWatcher() {
    while (true) {
        const {
            payload: { appId, userId },
            message,
        } = yield take(RESET_FACTS_FOR_USER);
        try {
            const isHashedUserId = yield select(selectApplicationTrackingSecurityHashUserId);

            const hashedUserId = isHashedUserId ? yield call(digestValue, userId) : userId;

            yield call(deleteFactsForUserRequest, appId, hashedUserId);
            yield put(pushNotificationToStack(message.success));
        } catch (err) {
            log.error(err);
            yield put(resetFactsForUserError());
            yield put(pushNotificationToStack(message.fail));
        }
    }
}

function* duplicateApplicationTask(payload, message) {
    const application = yield select(selectApplicationSettingsData);

    try {
        const resp = yield call(duplicateApplicationRequest, payload.sourceApplicationId, application);

        yield put(duplicateApplicationSuccess(resp.body.data, payload.sourceApplicationId));
        yield put(fetchApplicationList());
        yield put(pushNotificationToStack(message.success));
    } catch (err) {
        log.error(err);
        yield put(pushNotificationToStack(message.fail));
        yield put(duplicateApplicationError());
    }
}

function* duplicateApplicationWatcher() {
    while (true) {
        const { payload, message } = yield take(POST_DUPLICATE_APPLICATION);
        yield fork(duplicateApplicationTask, payload, message);
    }
}

function* updateApplicationAccessRequestTask({
    applicationId,
    access,
}: {
    applicationId: ApplicationId;
    access: ApplicationAccessData;
}) {
    try {
        yield call(updateApplicationAccess, applicationId, access);

        yield put(updateApplicationAccessSuccess(applicationId, access));
    } catch (err) {
        log.error(err);
        yield put(updateApplicationAccessFail());
    }
}

function* updateApplicationAccessRequestWatcher() {
    while (true) {
        const { payload } = yield take(UPDATE_APPLICATION_ACCESS_REQUEST);
        yield fork(updateApplicationAccessRequestTask, payload);
    }
}

function* updateApplicationIdentityRequestTask({
    applicationId,
    identity,
}: {
    applicationId: ApplicationId;
    identity: ApplicationIdentityDefinition;
}) {
    try {
        yield call(updateApplicationIdentity, applicationId, identity);

        yield put(updateApplicationIdentitySuccess(applicationId, identity));
    } catch (err) {
        log.error(err);
        yield put(updateApplicationIdentityFail());
    }
}

function* updateApplicationIdentityRequestWatcher() {
    while (true) {
        const { payload } = yield take(UPDATE_APPLICATION_IDENTITY_REQUEST);
        yield fork(updateApplicationIdentityRequestTask, payload);
    }
}

function* getRolloutPoliciesByMyOrganizationsTask() {
    try {
        const response = yield call(getRolloutPoliciesByMyOrganizations);
        const rolloutPolicies = JSON.parse(response.text);

        yield put(rolloutPoliciesByMyOrganizationsSuccess(rolloutPolicies));
    } catch (err) {
        log.error(err);
        yield put(rolloutPoliciesByMyOrganizationsError(err));
    }
}

function* getRolloutPoliciesBySpaceIdTask() {
    try {
        const spaceId = yield select(selectAppId);
        const response = yield call(getRolloutPoliciesBySpaceId, spaceId);
        const rolloutPolicies = JSON.parse(response.text);

        yield put(rolloutPoliciesBySpaceIdSuccess(rolloutPolicies));
    } catch (err) {
        log.error(err);
        yield put(rolloutPoliciesBySpaceIdError(err));
    }
}

function* rolloutPoliciesByMyOrganizationsRequestWatcher() {
    while (true) {
        yield take(ROLLOUT_POLICIES_BY_MY_ORGANIZATIONS_REQUEST);
        yield fork(getRolloutPoliciesByMyOrganizationsTask);
    }
}

function* rolloutPoliciesBySpaceIdRequestWatcher() {
    while (true) {
        yield take(ROLLOUT_POLICIES_BY_SPACE_ID_REQUEST);
        yield fork(getRolloutPoliciesBySpaceIdTask);
    }
}

function* updateApplicationThemeRequestTask({
    applicationId,
    theme,
}: {
    applicationId: ApplicationId;
    theme: ApplicationThemeDefinition;
}) {
    try {
        yield call(updateApplicationTheme, applicationId, theme);

        yield put(updateApplicationThemeSuccess(applicationId, theme));
    } catch (err) {
        log.error(`Unknown error for appId = ${applicationId} with theme = ${theme}. Reason: ${err}`);
        yield put(updateApplicationThemeFail());
    }
}

function* updateApplicationThemeRequestWatcher() {
    while (true) {
        const { payload } = yield take(UPDATE_APPLICATION_THEME_REQUEST);
        yield fork(updateApplicationThemeRequestTask, payload);
    }
}

function* updateApplicationExtensionUrlsRequestTask({
    applicationId,
    extensionUrls,
}: {
    applicationId: ApplicationId;
    extensionUrls: ExtensionUrl[];
}) {
    try {
        yield call(updateApplicationOriginUrls, applicationId, extensionUrls);

        yield put(updateApplicationExtensionUrlsSuccess(applicationId, extensionUrls));
    } catch (err) {
        log.error(
            `Unknown error for appId = ${applicationId} with extension urls = ${extensionUrls}. Reason: ${err}`
        );
        yield put(updateApplicationExtensionUrlsFail());
    }
}

function* updateApplicationOriginUrlsRequestWatcher() {
    while (true) {
        const { payload } = yield take(UPDATE_APPLICATION_EXTENSION_URLS_REQUEST);
        yield fork(updateApplicationExtensionUrlsRequestTask, payload);
    }
}

function* updateApplicationTrustedDomainsRequestTask({
    applicationId,
    trustedDomains,
}: {
    applicationId: ApplicationId;
    trustedDomains: ApplicationTrustedDomainValue[];
}) {
    try {
        yield call(updateApplicationTrustedDomains, applicationId, trustedDomains);

        yield put(updateApplicationTrustedDomainsSuccess(applicationId, trustedDomains));
    } catch (err) {
        log.error(
            `Unknown error for appId = ${applicationId} with trusted domains = ${trustedDomains}. Reason: ${err}`
        );
        yield put(updateApplicationTrustedDomainsFail());
    }
}

function* updateApplicationTrustedDomainsRequestWatcher() {
    while (true) {
        const { payload } = yield take(UPDATE_APPLICATION_TRUSTED_DOMAINS_REQUEST);
        yield fork(updateApplicationTrustedDomainsRequestTask, payload);
    }
}

export default function* settingsSaga() {
    yield respawn(importTranslationsWatcher);
    yield respawn(exportTranslationsWatcher);
    yield respawn(importAddOnTranslationsWatcher);
    yield respawn(exportAddOnTranslationsWatcher);
    yield respawn(duplicateApplicationWatcher);
    yield respawn(updateApplicationAccessRequestWatcher);
    yield respawn(updateApplicationIdentityRequestWatcher);
    yield respawn(updateApplicationOriginUrlsRequestWatcher);
    yield respawn(updateApplicationTrustedDomainsRequestWatcher);
    yield respawn(fetchInstalledListAddonsRequestWatcher);
    yield respawn(fetchAvailableListAddonsRequestWatcher);
    yield respawn(updateApplicationThemeRequestWatcher);
    yield respawn(rolloutPoliciesByMyOrganizationsRequestWatcher);
    yield respawn(rolloutPoliciesBySpaceIdRequestWatcher);

    yield all([
        updateDetailSettingsWatcher(),
        getApplicationSettingsDataWatcher(),
        fetchLanguagesListWatcher(),
        UsersAccessSaga(),
        inviteNewUserWatcher(),
        fetchAddOnOauthAuthorizationCodeUrlWatcher(),
        deleteAddOnWatcher(),
        createAddOnWatcher(),
        updateAddonWatcher(),
        loadArticlesPickerScriptWatcher(),
        generateGuideWatcher(),
        deleteAllFactsWatcher(),
        deleteFactsForUserWatcher(),
    ]);
}
